import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";

import {
  siteSessionAccessTokenKeyName,
  siteSessionExpiresAt,
  siteSessionIdTokenKeyName,
  siteSessionRefresh,
  siteSessionTokenKeyName,
} from "@/config/environment";
import { API_BASE_URL } from "@/config/network";
import { useBackendStatus } from "@/hooks/useBackendStatus";
import { resetSessionAndRedirectToSignIn } from "@/modules/auth/session";
import * as authServices from "@/services/auth";
import { isBackendUnavailableError } from "@/tools/httpErrors";

import { getCookie, setCookie } from "./cookies";

// Extend AxiosRequestConfig to include retry count
interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  __retryCount?: number;
}

const REFRESH_COOLDOWN_MS = 4 * 60 * 1000;
let refreshTokenPromise: Promise<string> | null = null;
let lastTokenRefreshedAt = 0;

const shouldRefreshToken = (): boolean => {
  return Date.now() - lastTokenRefreshedAt > REFRESH_COOLDOWN_MS;
};

const isTokenErrorResponse = (error: AxiosError): boolean => {
  const status = error.response?.status;

  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const data = error.response?.data;
  const message =
    typeof data === "string"
      ? data
      : data &&
          typeof data === "object" &&
          "message" in data &&
          typeof data.message === "string"
        ? data.message
        : "";

  return /token|jwt|expired|expirado|inv[aá]lido/i.test(message);
};

const refreshSessionToken = async (): Promise<string> => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  const refreshToken = getCookie(siteSessionRefresh);

  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  refreshTokenPromise = authServices
    .refreshToken(refreshToken)
    .then((response) => {
      if (!response?.idToken) {
        throw new Error("Invalid refresh token response");
      }

      const { accessToken, idToken, refreshToken: nextRefreshToken } = response;

      setCookie(siteSessionTokenKeyName, idToken);
      setCookie(siteSessionAccessTokenKeyName, accessToken);

      if (nextRefreshToken) {
        setCookie(siteSessionRefresh, nextRefreshToken);
      }

      setCookie(siteSessionIdTokenKeyName, idToken);

      lastTokenRefreshedAt = Date.now();
      return idToken;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });

  return refreshTokenPromise;
};

export const interceptor = () => {
  axios.interceptors.request.use(
    async (config) => {
      const sessionExpiresAt = getCookie(siteSessionExpiresAt);
      if (sessionExpiresAt && Date.now() > Number(sessionExpiresAt)) {
        resetSessionAndRedirectToSignIn();
        throw new Error("Session expired");
      }

      const siteSession = getCookie(siteSessionTokenKeyName);
      let token = siteSession;

      if (siteSession && shouldRefreshToken()) {
        try {
          token = await refreshSessionToken();
        } catch (error) {
          console.error("Token refresh failed");
          resetSessionAndRedirectToSignIn();
          return Promise.reject(error);
        }
      }

      config.headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : undefined,
        ...config.headers,
      } as AxiosRequestHeaders;
      config.baseURL ??= API_BASE_URL;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      if (isBackendUnavailableError(error)) {
        useBackendStatus.getState().markBackendUnavailable();
      }

      if (isTokenErrorResponse(error)) {
        const config = error.config as RetryableAxiosRequestConfig;
        const retryCount = config?.__retryCount || 0;
        const maxRetries = 1;

        if (retryCount >= maxRetries) {
          resetSessionAndRedirectToSignIn();
          return Promise.reject(error);
        }

        try {
          const idToken = await refreshSessionToken();

          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${idToken}`,
          };

          config.__retryCount = retryCount + 1;

          return axios.request(config);
        } catch {
          console.error("Token refresh failed");
          resetSessionAndRedirectToSignIn();
        }
      }

      return Promise.reject(error);
    },
  );
};
